'use strict';

const React = require('react');
const PropTypes = require('prop-types');
const Relay = require('react-relay/classic');
const classNames = require('classnames');
const get = require('lodash.get');
const tokenReplace = require('dibs-token-replace');

const { getFormattedPrice, filterQuote, sortQuote } = require('../shippingHelpers');
const Header = require('./Header').default;
const VerifiedTradePromo = require('../VerifiedTradePromo/VerifiedTradePromo').default;
const {
    applyPromo,
    promoCoversShipping,
} = require('../VerifiedTradePromo/verifiedTradePromoHelpers.es');
const { ViewerFragment } = require('../VerifiedTradePromo/verifiedTradePromoFragments.es');

const copy = require('./DetailsQuotes.json');
const shipmentMethodDefinitions = require('./shipmentMethodDefinitions.json');
const displayTransformations = require('../displayTransformations.json');
const styles = require('./DetailsQuotes.scss');

const REGION_BOUNDARIES = [
    'DOMESTIC',
    'REGIONAL',
    'DISTRICT',
    'METROPOLITAN',
    'GLOBAL',
    'REST_OF_WORLD',
];

function getQuoteData({ currency, hasUserId, userId, itemId, viewer }) {
    return function({ node }) {
        let method = get(node, 'serviceMethod.displayName');
        method = displayTransformations[method] || method;
        if (node.is1stdibsWhiteGloveQuote) {
            method = `1stdibs ${method} (Full Service)`;
        } else if (node.is1stdibsBilledQuote) {
            method = `1stdibs ${method}`;
        }

        const { string, abbreviation } = getFormattedPrice(
            get(node, 'buyerSubTotalAmount.convertedAmountList'),
            currency
        );
        let price = `${string} ${abbreviation}`;
        if (get(node, 'buyerSubTotalAmount.amount') === 0) {
            price = 'Complimentary';
        }
        const { buyerSubTotalAmount } = node;

        const serviceMethodType = get(node, 'serviceMethod.serviceMethodType');
        const serviceType = get(node, 'serviceMethod.serviceType');
        const methodDefinition = tokenReplace(
            shipmentMethodDefinitions[serviceType][serviceMethodType] || '',
            {
                facilitatedBy: node.is1stdibsBilledQuote ? '1stdibs' : 'seller',
            }
        );

        return {
            minTime: get(node, 'serviceMethod.windowMin.numberOfUnits'),
            regionBoundary: get(node, 'serviceMethod.region.regionBoundary.type.name'),
            shipTo: get(node, 'serviceMethod.region.regionName'),
            method,
            methodDefinition,
            shipTime: get(node, 'handlingTimeDisplay'),
            price,
            id: get(node, 'serviceId'),
            shippingPromo: {
                element: (
                    <VerifiedTradePromo
                        hasUserId={hasUserId}
                        userId={userId}
                        itemId={itemId}
                        viewer={viewer}
                        shippingAmount={buyerSubTotalAmount}
                        inShippingModal
                    />
                ),
                coversShipping: applyPromo(viewer) && promoCoversShipping(buyerSubTotalAmount),
            },
        };
    };
}

function DataPoint({ value, valueClass, dataTn, className = 'colLg12', infoElement = null }) {
    return (
        <div className={classNames(className, 'colSm12', styles.dataPoint)}>
            <div className={styles.value}>
                <span className={valueClass} data-tn={`${dataTn}-value`}>
                    {' '}
                    {value}{' '}
                </span>
                {infoElement}
            </div>
        </div>
    );
}

DataPoint.propTypes = {
    hasUserId: PropTypes.bool,
    className: PropTypes.string,
    value: PropTypes.string,
    valueClass: PropTypes.string,
    dataTn: PropTypes.string,
    infoElement: PropTypes.node,
};

function Quote(quote) {
    const { shippingPromo = {} } = quote;
    const priceClass = classNames(styles.price, {
        [styles.applyFreeShipping]: shippingPromo.coversShipping,
    });

    return (
        <div
            data-tn="shipping-row"
            key={`quote-${quote.id}`}
            className={classNames('rowFlex', 'colSm12', styles.quote)}
        >
            <div className={classNames('colLg9', styles.column)}>
                <DataPoint dataTn="ship-method" className={styles.method} value={quote.method} />
                <DataPoint dataTn="ship-method-definition" value={quote.methodDefinition} />
            </div>
            <div className={classNames('colLg2', styles.column)}>
                <DataPoint dataTn="ship-time" value={quote.shipTime} />
                <DataPoint
                    dataTn="ship-price"
                    value={quote.price}
                    valueClass={priceClass}
                    infoElement={shippingPromo.element}
                />
            </div>
        </div>
    );
}

Quote.propTypes = {
    quote: PropTypes.shape({
        shipTo: PropTypes.string,
        method: PropTypes.string,
        methodDefinition: PropTypes.string,
        shipTime: PropTypes.string,
        price: PropTypes.string,
        shippingPromo: PropTypes.object,
    }).isRequired,
    index: PropTypes.number.isRequired,
};

function Quotes({ quotes = [], currency, hasUserId, userId, itemId, viewer, item }) {
    const filteredQuotes = quotes
        .filter(filterQuote)
        .sort(sortQuote)
        .map(
            getQuoteData({
                currency,
                hasUserId,
                userId,
                itemId,
                viewer,
            })
        );

    if (!filteredQuotes.length) {
        return null;
    }
    if (filteredQuotes[0].shipTo === copy.restOfWorld) {
        if (filteredQuotes.length === 1) {
            filteredQuotes[0].shipTo = copy.alone;
        } else {
            filteredQuotes[0].shipTo = copy.withOthers;
        }
    }

    // group quotes by regionName
    const groupedQuoteObj = filteredQuotes.reduce((groups, quote) => {
        const regionName = quote.shipTo;
        if (!regionName) {
            return groups;
        }

        groups[regionName] = groups[regionName] || [];
        groups[regionName].push(quote);
        return groups;
    }, {});

    // convert groupedQuoteObj into array
    const groupedQuotes = Object.keys(groupedQuoteObj).map(key => {
        return groupedQuoteObj[key];
    });

    // sort array by regionBoundary then by regionName
    groupedQuotes.sort((a, b) => {
        const aBoundary = get(a[0], 'regionBoundary');
        const bBoundary = get(b[0], 'regionBoundary');
        const aName = get(a[0], 'shipTo');
        const bName = get(b[0], 'shipTo');
        const boundarySort =
            REGION_BOUNDARIES.indexOf(aBoundary) - REGION_BOUNDARIES.indexOf(bBoundary);

        if (boundarySort) {
            return boundarySort;
        }
        if (aName < bName) {
            return -1;
        }
        if (aName > bName) {
            return 1;
        }
        return 0;
    });

    return (
        <div>
            <Header item={item} />
            {groupedQuotes.map(q => {
                const shipTo = q[0].shipTo;
                return (
                    <div
                        key={`quote-region-${shipTo}`}
                        data-tn={`quote-region-${shipTo}`}
                        className={styles.wrapper}
                    >
                        <div className={styles.boundaryHeader}>{shipTo}</div>
                        {q.map(Quote)}
                    </div>
                );
            })}
        </div>
    );
}

Quotes.propTypes = {
    quotes: PropTypes.array.isRequired,
    currency: PropTypes.string.isRequired,
    hasUserId: PropTypes.bool,
    userId: PropTypes.string,
    itemId: PropTypes.string,
    viewer: PropTypes.object,
    item: PropTypes.object,
};

module.exports = Relay.createContainer(Quotes, {
    initialVariables: {
        hasUserId: false,
        userId: null,
        itemId: null,
    },
    fragments: {
        item: () => Relay.QL`
            fragment on Item {
                ${Header.getFragment('item')}
            }
        `,
        viewer: ({ hasUserId, itemId, userId }) => Relay.QL`
            fragment on Viewer {
                ${VerifiedTradePromo.getFragment('viewer', { hasUserId, itemId, userId })}
                ${ViewerFragment}
            }
        `,
    },
});



// WEBPACK FOOTER //
// ./assets/js/newPdp/components/Shipping/ShippingDetailsModal/DetailsQuotes.jsx